import { termMaxConfigByChain, termMaxLenderKey } from '@1delta/data-sdk'
import { TermMaxOracleAggregatorV2Abi } from '@1delta/abis'
import {
  FetcherResult,
  MulticallCall,
  OraclePriceEntry,
  ParseContext,
} from '../types'
import { createMarketUid } from '../../../utils'
import { getCachedTermMaxMarkets } from '../../../lending/public-data/termmax/fetchPublic'

/**
 * TermMax oracle price fetcher.
 *
 * `OracleAggregatorV2.getPrice(asset)` returns a **direct USD price** with its
 * own decimals — no ratio, no base asset, nothing to derive. That makes this the
 * simplest fetcher in the set, and unlike Morpho/Midnight/Teller its entries are
 * NOT derived, so they compete with (rather than defer to) other direct reads.
 *
 * Worth having even where the generic pipeline already prices the token: this is
 * the feed TermMax ITSELF computes LTV and liquidation against, so it is the
 * authoritative price for that market's risk. It is Chainlink-shaped underneath
 * but adds a backup aggregator with an independent heartbeat and optional
 * min/max caps (the Aave `PriceCapAdapter` pattern), so it can legitimately
 * disagree with a raw feed read — and when it does, TermMax's number is the one
 * that decides whether a position is liquidatable.
 *
 * `getPrice` REVERTS (`OracleIsNotWorking`) when both the primary and backup
 * feeds are stale. `allowFailure` on the multicall turns that into a null result
 * which we skip — a revert means "no price", never zero.
 *
 * Market discovery is dynamic, so this reads the cache the public fetch keeps
 * warm rather than a static registry. If the public fetch has not run yet for
 * this chain the fetcher emits nothing and prices fall back to the generic
 * pipeline — a degradation, not a failure.
 */

/** Grouped metadata for one market: two `getPrice` calls (debt, collateral). */
interface TermMaxMarketOracleMeta {
  market: string
  debtToken: string
  collateral: string
}

/**
 * Build multicall calls for TermMax oracles. One `FetcherResult` per live
 * market, each with `[getPrice(debtToken), getPrice(collateral)]` against the
 * chain's `OracleAggregatorV2`. `lender` is the market's `TERMMAX_<ADDR>` key so
 * entries join the lending data from `convertTermMaxMarketsToResponse`.
 */
export function getTermMaxCalls(
  chainId: string,
): FetcherResult<TermMaxMarketOracleMeta>[] {
  const proto = termMaxConfigByChain(chainId)
  if (!proto?.oracleAggregatorV2) return []

  const markets = getCachedTermMaxMarkets(chainId)
  if (markets.length === 0) return []

  const oracle = proto.oracleAggregatorV2
  const results: FetcherResult<TermMaxMarketOracleMeta>[] = []
  for (const m of markets) {
    if (!m.market || !m.debtToken || !m.collateral) continue
    const calls: MulticallCall[] = [
      { address: oracle, name: 'getPrice', params: [m.debtToken] },
      { address: oracle, name: 'getPrice', params: [m.collateral] },
    ]
    results.push({
      calls,
      meta: {
        market: m.market.toLowerCase(),
        debtToken: m.debtToken.toLowerCase(),
        collateral: m.collateral.toLowerCase(),
      },
      lender: termMaxLenderKey(m.market),
    })
  }
  return results
}

/** `getPrice` → `(uint256 price, uint8 decimals)`; USD = price / 10^decimals. */
function readUsdPrice(res: any): number | undefined {
  if (res == null || res === '0x') return undefined
  let raw: any
  let dec: any
  if (Array.isArray(res)) {
    raw = res[0]
    dec = res[1]
  } else if (typeof res === 'object') {
    raw = res.price
    dec = res.decimals
  } else {
    return undefined
  }
  let price: bigint
  try {
    price = BigInt(raw)
  } catch {
    return undefined
  }
  if (price <= 0n) return undefined
  const decimals = Number(dec)
  if (!Number.isFinite(decimals) || decimals < 0 || decimals > 36) return undefined
  const usd = Number(price) / 10 ** decimals
  return Number.isFinite(usd) && usd > 0 ? usd : undefined
}

/**
 * Parse TermMax oracle results: `data[0]` = debt-token USD, `data[1]` =
 * collateral USD. Both are direct reads, so `price` and `priceUSD` are the same
 * number and no `baseAsset` is set. A reverted (null) leg is skipped rather than
 * emitted as zero.
 */
export function parseTermMaxResults(
  data: any[],
  meta: TermMaxMarketOracleMeta,
  context: ParseContext,
): OraclePriceEntry[] {
  const { chainId } = context
  const lenderKey = termMaxLenderKey(meta.market)
  const entries: OraclePriceEntry[] = []

  const legs: Array<[string, any, string]> = [
    [meta.debtToken, data?.[0], 'TermMax oracle (debt asset)'],
    [meta.collateral, data?.[1], 'TermMax oracle (collateral)'],
  ]

  for (const [asset, res, description] of legs) {
    const usd = readUsdPrice(res)
    if (usd === undefined) continue
    entries.push({
      asset,
      price: usd,
      priceUSD: usd,
      marketUid: createMarketUid(chainId, lenderKey, asset),
      targetLender: lenderKey,
      description,
    })
  }

  return entries
}

/** ABI for the TermMax oracle aggregator. */
export function getTermMaxAbi() {
  return TermMaxOracleAggregatorV2Abi
}

/** TermMax Oracle Fetcher module. */
export const termMaxFetcher = {
  getCalls: getTermMaxCalls,
  parse: parseTermMaxResults,
  getAbi: getTermMaxAbi,
}
